import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { Direction, ScoreMode } from './hub';

/** PD targets and reflective practice (backend services/development.ts). */

export type Audience =
  | { type: 'everyone' }
  | { type: 'career'; value: string }
  | { type: 'subject'; value: string }
  | { type: 'age'; value: string }
  | { type: 'people'; ids: string[] };

export interface ReflectionModel {
  id: string;
  name: string;
  summary: string | null;
  source: string;
  forLessons: boolean;
  prompts: { key: string; question: string; hint: string | null }[];
}

export interface DevelopmentOptions {
  models: ReflectionModel[];
  indicators: { id: string; name: string; domainId: string; domainName: string }[];
  priorities: { text: string; indicatorIds: string[] }[];
  groups: {
    careerStages: { id: string; label: string; people: number }[];
    subjects: { value: string; people: number }[];
    ages: { value: string; people: number }[];
    people: { id: string; name: string }[];
  };
  term: { label: string; from: string; to: string };
  suggestedCheckpoints: { label: string; dueOn: string }[];
}

export interface Target {
  id: string;
  title: string;
  why: string | null;
  priorityText: string | null;
  status: 'active' | 'archived';
  startsOn: string;
  endsOn: string;
  audience: Audience;
  audienceLabel: string;
  audienceSize: number;
  model: { id: string; name: string; prompts: ReflectionModel['prompts'] } | null;
  indicators: { id: string; name: string; average: number | null; direction: Direction | null; teachers: number }[];
  checkpoints: {
    id: string;
    label: string;
    dueOn: string;
    state: 'open' | 'upcoming';
    reflected: number;
    /** Null until enough people have reflected that no one's answer can be picked out. */
    tried: { yes: number; partly: number; notYet: number } | null;
  }[];
  reflectedPeople: number;
  shared: { id: string; name: string; checkpointId: string | null; tried: string | null; answers: Record<string, string>; at: string }[];
}

export interface TargetInput {
  title: string;
  why: string;
  priorityText: string | null;
  reflectionModelId: string;
  audience: Audience;
  indicatorIds: string[];
  startsOn: string;
  endsOn: string;
  checkpoints: { id?: string; label: string; dueOn: string }[];
}

export function useDevelopmentOptions() {
  return useQuery({ queryKey: ['development-options'], queryFn: () => api<DevelopmentOptions>('/development/options') });
}

export function useTargets() {
  return useQuery({
    queryKey: ['development-targets'],
    queryFn: () => api<{ targets: Target[]; minGroup: number; scoreMode?: ScoreMode }>('/development/targets'),
  });
}

export function useTeamLog() {
  return useQuery({
    queryKey: ['development-team-log'],
    queryFn: () => api<{ year: string; totalMinutes: number; people: { id: string; name: string; minutes: number; entries: number }[] }>('/development/team-log'),
  });
}

export function useSaveTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: TargetInput }) =>
      id
        ? api<{ id: string }>(`/development/targets/${id}`, { method: 'PUT', body: JSON.stringify(input) })
        : api<{ id: string }>('/development/targets', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['development-targets'] }),
  });
}

export function useArchiveTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      api(`/development/targets/${id}/archive`, { method: 'POST', body: JSON.stringify({ archived }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['development-targets'] }),
  });
}

export interface PdSession {
  id: string;
  title: string;
  happensOn: string;
  minutes: number;
  location: string | null;
  details: string | null;
  target: { id: string; title: string } | null;
  attended: number;
}

export type PdSessionInput = { title: string; happensOn: string; minutes: number; location: string; details: string; targetId: string | null };

export function useSessions() {
  return useQuery({ queryKey: ['development-sessions'], queryFn: async () => (await api<{ data: PdSession[] }>('/development/sessions')).data });
}

export function useSaveSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id?: string; input: PdSessionInput }) =>
      id
        ? api(`/development/sessions/${id}`, { method: 'PUT', body: JSON.stringify(input) })
        : api('/development/sessions', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['development-sessions'] }),
  });
}

export function useDeleteSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/development/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['development-sessions'] }),
  });
}

/** EducAitor admins: every reflection model, including partner programmes and inactive ones. */
export type AdminReflectionModel = ReflectionModel & { active: boolean; sortOrder: number };

export function useAdminModels() {
  return useQuery({ queryKey: ['admin-reflection-models'], queryFn: async () => (await api<{ data: AdminReflectionModel[] }>('/development/admin/models')).data });
}

export function useSaveModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<AdminReflectionModel>) => api<{ id: string }>('/development/admin/models', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-reflection-models'] }),
  });
}

export const hours = (minutes: number) => {
  if (!minutes) return '0 hours';
  const h = minutes / 60;
  return h < 1 ? `${minutes} min` : `${Number.isInteger(h) ? h : h.toFixed(1)} ${h === 1 ? 'hour' : 'hours'}`;
};

export const dayMonth = (ymd: string) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
